/**
 * Where a page load asks the harness's shell to open: a Settings section, or a global panel.
 *
 * Orik's legacy pages are now surfaces of the harness's shell — Settings is its settings panel, and
 * Scheduled tasks is `ui-schedule`'s page — but their URLs stay valid: account connections return to
 * `/settings`, and links and bookmarks name `/tasks`. The app reads the URL once and records the request here
 * before the harness boots; the shell's occupants take it when they mount (`src/components/harness-shell.tsx`).
 */

/** The Settings section Orik's integrations live in. */
export const INTEGRATIONS_SECTION = "atlas-integrations";
/** The Settings section with the account and sign-out. */
export const ACCOUNT_SECTION = "orik-account";
/** `ui-schedule`'s page in the `main` panel set. */
export const SCHEDULES_PANEL = "schedules";

export interface ShellLink {
  /** A Settings section to open. */
  readonly settingsSection?: string;
  /** A global panel to show in place of the conversation. */
  readonly panel?: string;
  /** The provider whose account connection just failed (`/settings?error=<provider>`). */
  readonly integrationError?: string;
}

let pendingSection: string | undefined;
let integrationError: string | undefined;

/** Record what this page load asks for. */
export function requestShellLink(link: ShellLink): void {
  pendingSection = link.settingsSection;
  integrationError = link.integrationError;
}

/** Whether a Settings section is waiting to be opened. */
export function hasPendingSettingsSection(): boolean {
  return pendingSection !== undefined;
}

/** The Settings section to open, once: a later open of Settings is the user's own. */
export function takePendingSettingsSection(): string | undefined {
  const section = pendingSection;
  pendingSection = undefined;
  return section;
}

/** The provider whose connection failed on the way back to this page, if any. */
export function pendingIntegrationError(): string | undefined {
  return integrationError;
}

/** The URL a page load that named a link is left at, so a reload does not repeat it. */
export function settledUrl(location: Pick<Location, "search" | "hash">): string {
  const query = new URLSearchParams(location.search);
  query.delete("error");
  const search = query.toString();
  return `/${search === "" ? "" : `?${search}`}${location.hash}`;
}
